import {Component, OnDestroy, OnInit} from '@angular/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {Title} from '@angular/platform-browser';
import {SubscriptionService} from "../../../shared/services/subscription.service";
import {_windowUtil} from "../../utils/ui-utils"
import { properties } from 'src/environments/properties';
import { B4UPLUS_ROUTES } from '../../constants/b4uplus-routes';

declare let $: any;
declare let AB: any;

@Component({
    selector: 'app-foruplus-faq',
    templateUrl: './foruplus-faq.component.html',
    styleUrls: ['./foruplus-faq.component.scss'],
    providers: [SubscriptionService]
})
export class ForuplusFaqComponent implements OnInit, OnDestroy {
    currentEnvironment;
    frequentlyAskedQuestion = '';
    public windowUtil = _windowUtil(window);
    properties = properties;

    // The native-app FAQ webview (/foruplus/mobile/faqs) shows no action buttons; the guarded
    // in-app /foruplus/manage/foruplus-faq route keeps them.
    showActionButtons = true;

    // This is the ForU+ FAQ page, so always use the ForU+ copy block. (The legacy `enableForUPlus`
    // rebrand flag stays false for this launch — gating on it would show FreshPass copy here.)
    programDisplayData = this.properties.programType.forUPlus;

    private destroy$ = new Subject<void>();

    constructor(
        public subscriptionService: SubscriptionService,
        private titleService: Title) {
        this.titleService.setTitle(`${this.programDisplayData.title} - Frequently Asked Questions`);
    }

    ngOnInit(): void {
        this.showActionButtons = window.location.pathname.indexOf('/foruplus/mobile') < 0;
        this.currentEnvironment = this.subscriptionService.getEnvironment();
        this.subscriptionService.getB4UPlusFrequentlyAskedComponent(this.currentEnvironment)
            .pipe(takeUntil(this.destroy$))
            .subscribe(data => {
            if (data) {
                this.frequentlyAskedQuestion = data;
                setTimeout(() => {
                    // Add inactive-header to all accordion headers
                    const headers = document.querySelectorAll('.accordion-header');
                    headers.forEach(header => header.classList.add('inactive-header'));
                    // Add role="text" to .addRole elements
                    const addRoleEls = document.querySelectorAll('.addRole');
                    addRoleEls.forEach(el => el.setAttribute('role', 'text'));
                    // Handle display types
                    const displayTypes = document.querySelectorAll('.display-type');
                    displayTypes.forEach((el: Element) => {
                        const displayType = el.getAttribute('data-display-type');
                        const headerEls = el.querySelectorAll('.accordion-header');
                        const contentEls = el.querySelectorAll('.accordion-content');
                        if (displayType === 'expandFirst' && headerEls.length > 0 && contentEls.length > 0) {
                            headerEls[0].classList.add('active-header');
                            headerEls[0].classList.remove('inactive-header');
                            contentEls[0].classList.add('open-content');
                            (contentEls[0] as HTMLElement).style.display = 'block';
                        } else if (displayType === 'expanded') {
                            headerEls.forEach(header => {
                                header.classList.add('active-header');
                                header.classList.remove('inactive-header');
                            });
                            contentEls.forEach(content => {
                                content.classList.add('open-content');
                                (content as HTMLElement).style.display = 'block';
                            });
                        }
                    });
                    // Set aria-expanded and add event listeners
                    headers.forEach(header => {
                        header.setAttribute('aria-expanded', header.classList.contains('active-header') ? 'true' : 'false');
                        header.addEventListener('click', () => {
                            const isInactive = header.classList.contains('inactive-header');
                            header.classList.toggle('active-header', isInactive);
                            header.classList.toggle('inactive-header', !isInactive);
                            header.setAttribute('aria-expanded', isInactive ? 'true' : 'false');
                            // The AEM XF nests <h3>/<p> inside <h2>, which browsers auto-close,
                            // making nextElementSibling the inner heading rather than .accordion-content.
                            // Walk up to .accordion-item to find the content panel reliably.
                            const content = header.closest('.accordion-item')?.querySelector('.accordion-content') as HTMLElement | null;
                            if (content) {
                                content.classList.toggle('open-content');
                                content.style.display = content.classList.contains('open-content') ? 'block' : 'none';
                            }
                        });
                        header.addEventListener('keypress', (e: KeyboardEvent) => {
                            if (e.key === 'Enter' || e.which === 13) {
                                const isInactive = header.classList.contains('inactive-header');
                                header.classList.toggle('active-header', isInactive);
                                header.classList.toggle('inactive-header', !isInactive);
                                header.setAttribute('aria-expanded', isInactive ? 'true' : 'false');
                                const content = header.closest('.accordion-item')?.querySelector('.accordion-content') as HTMLElement | null;
                                if (content) {
                                    content.classList.toggle('open-content');
                                    content.style.display = content.classList.contains('open-content') ? 'block' : 'none';
                                }
                            }
                        });
                    });
                }, 2000);
            }
        });
    }

    openManageForUPlus() {
        // Canonical ForU+ manage destination. urlPath.MANAGE_PLAN_PATH is derived from the global
        // enableForUPlus flag (false this launch), so it would resolve to /freshpass/manage here.
        this.windowUtil.setWindowLocationHref(B4UPLUS_ROUTES.MANAGE_PLAN);
    }

    openHomepage() {
        this.windowUtil.setWindowLocationHref(this.programDisplayData?.urlPath?.SHOP_HOME_PATH);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
